import React, { useId } from "react";
import clsx from "clsx";

export interface CheckboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "value" | "onChange" | "checked"
  > {
  /** Current checked state. */
  checked: boolean;
  /** Called with the new checked state when the user toggles. */
  onChange: (checked: boolean) => void;
  /** Optional label shown next to the checkbox. */
  label?: React.ReactNode;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      name,
      label,
      id: idProp,
      disabled = false,
      className,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const handleChange = () => {
      onChange(!checked);
    };

    return (
      <div className={clsx("flex items-center gap-3", className)}>
        <input
          ref={ref}
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className="peer sr-only"
          {...rest}
        />
        <label
          htmlFor={id}
          className={clsx(
            "flex items-center gap-3 cursor-pointer select-none",
            disabled && "cursor-not-allowed opacity-70"
          )}
        >
          <span
            aria-hidden="true"
            className={clsx(
              "flex items-center justify-center shrink-0 w-5 h-5 rounded border transition-colors",
              checked ? "border-lifeguards-blue bg-lifeguards-blue" : "border-gray-400 bg-white",
              !disabled &&
                "peer-focus-visible:ring-2 peer-focus-visible:ring-lifeguards-blue peer-focus-visible:ring-offset-2",
              !disabled && !checked && "hover:border-gray-500"
            )}
          >
            {checked && (
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            )}
          </span>
          {label != null && (
            <span className="text-sm text-gray-700">{label}</span>
          )}
        </label>
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";
